import type { ExperimentsMonitorManager } from '@/state/experiments/useExperimentsMonitor';
import { useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { ActionButton, EmptyState, Pager, Panel } from '../ConsolePanel';
import { ExperimentRow, ExperimentRowHead, rowMinWidth } from './ExperimentRow';

export type TrendTableProps = {
  manager: ExperimentsMonitorManager;
  trendType: 'positive' | 'negative';
};

export const TrendTable = ({ manager, trendType }: TrendTableProps) => {
  const { filteredPositive, filteredNegative, expandedTable, setExpandedTable } = manager;
  const { activeCount, positiveCount, negativeCount, scopeTab, search } = manager;
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 6;

  const experiments = trendType === 'positive' ? filteredPositive : filteredNegative;
  const isExpanded = expandedTable === trendType;
  const title = trendType === 'positive' ? 'Positive Experiments' : 'Negative Experiments';
  const subtitle =
    trendType === 'positive'
      ? 'Successful experiments with positive impact.'
      : 'Experiments that resulted in negative outcomes.';
  const otherTableName = trendType === 'positive' ? 'Negative' : 'Positive';

  /*
    Both lists arrive already narrowed twice — by the scope pills and by the search box —
    so "No {trendType} experiments found." was a claim about the account made from a
    filtered set. Ordered by how the narrowing actually happens: scope, then the trend
    split, then the search, so each answer is about the step that emptied the list.
  */
  const trendCount = trendType === 'positive' ? positiveCount : negativeCount;
  const emptyReason = (() => {
    if (activeCount === 0) {
      if (scopeTab === 'mine') return 'No experiments were created or started by you.';
      if (scopeTab === 'account') return 'No experiments in the account you selected.';
      return 'No experiments have reported any data yet.';
    }
    // No threshold in the copy: the conversion rate that counts as a trend lives in
    // useExperimentsMonitor, and repeating the number here would let the two drift.
    if (trendCount === 0) {
      return `None of the ${activeCount} experiment${activeCount === 1 ? '' : 's'} in this scope are trending ${trendType}.`;
    }
    if (search) {
      return `Nothing matches "${search}" — ${trendCount} ${trendType} experiment${trendCount === 1 ? '' : 's'} in this scope.`;
    }
    return `No ${trendType} experiments on this page.`;
  })();

  // The product tour's row anchor: the positive table's first row, or the negative one's
  // when the positive table is empty or not on screen.
  const anchorsRows =
    trendType === 'positive' ? filteredPositive.length > 0 : filteredPositive.length === 0 || isExpanded;

  const totalPages = Math.max(1, Math.ceil(experiments.length / perPage));
  const paginatedExperiments = experiments.slice((currentPage - 1) * perPage, currentPage * perPage);

  const toggleRow = (rowId: string) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  };

  const onToggleExpand = () => {
    setExpandedTable(isExpanded ? null : trendType);
  };

  const onSwitchTable = () => {
    setExpandedTable(trendType === 'positive' ? 'negative' : 'positive');
  };

  return (
    <Panel
      className="self-start"
      title={title}
      description={subtitle}
      bodyClassName="p-0"
      actions={
        <>
          {isExpanded && (
            <ActionButton className="hidden sm:flex" onClick={onSwitchTable}>
              <Maximize2 className="size-3" strokeWidth={1.5} />
              {otherTableName}
            </ActionButton>
          )}
          <ActionButton className="hidden sm:flex" onClick={onToggleExpand}>
            {isExpanded ? (
              <Minimize2 className="size-3" strokeWidth={1.5} />
            ) : (
              <Maximize2 className="size-3" strokeWidth={1.5} />
            )}
            {isExpanded ? 'Collapse' : 'Expand'}
          </ActionButton>
        </>
      }
    >
      {/* One scroll container, not two: an inner horizontal scroller would break
          the sticky head, which is the only thing keeping the columns readable
          six rows down. */}
      <div className="max-h-[70vh] min-h-[220px] overflow-auto">
        <div style={{ minWidth: `${rowMinWidth(true)}px` }}>
          <div className="sticky top-0 z-10">
            <ExperimentRowHead showDomain />
          </div>

          {paginatedExperiments.length === 0 ? (
            <div className="p-3.5">
              <EmptyState>{emptyReason}</EmptyState>
            </div>
          ) : (
            paginatedExperiments.map((exp, idx) => (
              <ExperimentRow
                key={exp.id}
                experiment={exp}
                tourAnchor={anchorsRows && idx === 0 ? 'performance-row' : undefined}
                isExpanded={expandedRowId === `trend-${exp.id}`}
                onToggle={() => toggleRow(`trend-${exp.id}`)}
                showDomain
              />
            ))
          )}
        </div>
      </div>

      {/* `totalPages` floors at 1, so without this an empty table still drew
          "Page 1 of 1" under four permanently disabled arrows. */}
      {totalPages > 1 && <Pager currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />}
    </Panel>
  );
};
